import { peekPullRequestRedirectPath } from "../pullRequestRedirect";
import { accountIdFromPath, siblingAccountId } from "../util/accountId";
import { BASE_PATH, stripBasePath } from "../util/basePath";
import authStore, {
    enterInspectFromEntry,
} from "./accessToken/observables/authStore";
import { authenticate } from "./auth";
import { getInspectToken } from "./inspectTokens";
import { getCognitoSession } from "./localStorage";

/**
 * The `/:accountId` this tab is for. On a pull request build a hard navigation
 * to a deep path is served master's index.html by cloudfront, bounces through
 * RedirectToPullRequestRoute and lands on the PR base path with the real target
 * parked in local storage -- so the account id is there, not in the url yet.
 */
const targetAccountId = (pathname: string) => {
    const accountId = accountIdFromPath(stripBasePath(pathname));
    if (accountId || !BASE_PATH) {
        return accountId;
    }
    return accountIdFromPath(peekPullRequestRedirectPath() || "");
};

/**
 * Authenticate the base (logged-in) session, then resume inspect mode for this
 * tab if its `/:accountId` has a stored inspect token. Returns "app" to render
 * the app or "login" to show the login screen.
 */
export const bootstrapAuth = async (
    pathname: string,
): Promise<"app" | "login"> => {
    const baseSession = getCognitoSession();
    const authResult = await authenticate(baseSession);
    if (!authResult) {
        return "login";
    }

    authStore.dispatch("setAuthenticatedUser", { accountUser: authResult });

    const accountId = targetAccountId(pathname);
    if (accountId) {
        let entry = getInspectToken(accountId);
        if (!entry) {
            // The tab may have toggled env (P<->T) client-side after inspect
            // began; that navigation never wrote an entry for the new mode. If
            // the sibling mode has one, resume inspect in the current mode by
            // minting a fresh exchange token as the partner in this mode (the
            // empty token forces enterInspectFromEntry to re-mint).
            const sibling = siblingAccountId(accountId);
            const siblingEntry = sibling ? getInspectToken(sibling) : undefined;
            if (siblingEntry) {
                entry = {
                    token: "",
                    exp: 0,
                    partnerId: `${accountId[0]}${siblingEntry.partnerId.substring(1)}`,
                };
            }
        }
        if (entry) {
            try {
                await enterInspectFromEntry(accountId, entry);
            } catch (error) {
                // Inspect re-entry is best-effort: a failed exchange/account
                // call must not reject bootstrap, or the index.tsx catch clears
                // the base session and logs the admin out. Fall through to the
                // already-authenticated base session (normal mode) instead.
                console.error("Failed to resume inspect mode", error);
            }
        }
    }
    return "app";
};
